import {
  NumberField,
  SelectField,
  SwitchField,
  TextField,
} from '~/common/components';
import { usePurchaseContext } from '~/purchase/context';
import { useI18n, Trans, useLocale } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  Col,
  Flex,
  FormContainer,
  Icon,
  Row,
  Text,
  Title,
  Tooltip,
} from '@wyz/ui';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { DEFAULT_EURO_SIGN } from '@wyz/utils';
import {
  QuoteRefinementTable,
  useQuoteServicePricingPolicyForm,
} from '~/purchase/components';
import {
  CommonPricingPolicyHelpers,
  getQuoteServicePriceTypeTranslation,
  QuoteServicePricingPolicyHelpers,
} from '~/purchase/helpers';
import {
  PP_CUSTOM_CONSTRAINT,
  PRICE_TYPE_ENUM,
  PRICING_STATUS_ENUM,
  quoteServiceMountingStepFormSchema,
} from '~/purchase/schemas';
import {
  IGlobalQuoteServicePricingFormData,
  IPricingStatus,
  IQuoteServiceMountingStepForm,
} from '~/purchase/types';
import { useValidationForm } from '~/common/hooks';
import { IOptionItem, ValidationErrorResponse } from '@wyz/types';
import { CommonErrorCodesEnumType } from '~/common/types';

type CommonFormProps = {
  globalFormData: IGlobalQuoteServicePricingFormData;
};
type MountingStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IQuoteServiceMountingStepForm,
    status: IPricingStatus,
  ) => void;
  errors?: null | ValidationErrorResponse<CommonErrorCodesEnumType>;
};

const QuoteMountingStepForm: React.FC<MountingStepFormProps> = ({
  onSubmit,
  globalFormData,
  errors,
}) => {
  const { t } = useI18n();
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const methods = useForm<IQuoteServiceMountingStepForm>({
    defaultValues: globalFormData.MOUNTING,
    shouldUnregister: true,
    resolver: zodResolver(quoteServiceMountingStepFormSchema),
  });

  useValidationForm({
    methods: methods,
    errors: errors?.errors,
    t,
  });

  const watchedRefinementFlags = useWatch({
    control: methods.control,
    name: 'refinementFlags',
  });

  const hasRefinement = QuoteServicePricingPolicyHelpers.hasRefined(
    watchedRefinementFlags,
  );

  const { state } = usePurchaseContext();
  const locale = useLocale();
  const currencySymbol = state.targetCustomer?.deliveryInformation?.country
    .currencyIsoCode
    ? locale.formatters.currency(
        state.targetCustomer?.deliveryInformation?.country.currencyIsoCode,
      )
    : DEFAULT_EURO_SIGN;

  const watchedPriceType = useWatch({
    name: 'priceType',
    control: methods.control,
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData, event) => {
        onSubmit(
          formData,
          ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
            'data-status',
          ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
        );
      }}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
      id={QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX.MOUNTING.toString()}
    >
      <Card>
        <Card.Header>
          <Card.Title>
            <Title heading='xl' weight='bold'>
              <Trans i18nKey='app.quote.pricing_policy.service.mounting.title' />
            </Title>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className='mt-3'>
            <Col xs={6}>
              <TextField
                name='name'
                label={<Trans i18nKey='app.common.pricing_policy.name' />}
                type='text'
                maxLength={PP_CUSTOM_CONSTRAINT.NAME_MAX_LENGTH}
                size='sm'
                required
              />
            </Col>
            <Col xs={4}>
              <SelectField
                name='vehicleTypeGroup'
                label={
                  <Trans i18nKey='app.quote.pricing_policy.form.step1.vehicle_type_group' />
                }
                options={pricingPolicyForm.vehicleTypeGroups.optionItems}
                size='sm'
                required
              />
            </Col>
            <Col xs={2}>
              <SelectField<false, IOptionItem<string>>
                name='priceType'
                label={t(
                  'app.quote.pricing_policy.form.step1.price_type.label',
                )}
                options={Object.values(PRICE_TYPE_ENUM).map((type) => ({
                  label: t(getQuoteServicePriceTypeTranslation(type)),
                  value: type,
                }))}
                required
              />
            </Col>
          </Row>
          <Row className='mt-3'>
            <Row>
              <Col lg={2} className='pe-0 d-flex align-items-center'>
                <Text heading='normal' weight='bold'>
                  <Trans i18nKey='app.quote.pricing_policy.form.step1.pricing_configuration' />
                </Text>
              </Col>
              <Col lg={2}>
                {!hasRefinement && (
                  <Flex alignItems='center'>
                    <NumberField
                      name={`globalValue`}
                      label=''
                      withKnobs={false}
                      min={0}
                      max={100}
                      placeholder='XX'
                      size='sm'
                      feedbackType='tooltip'
                      sign={currencySymbol}
                    />
                    <Text
                      color='secondary-gray'
                      weight='bold'
                      heading='xs'
                      className='p-2'
                    >
                      <Trans
                        i18nKey={getQuoteServicePriceTypeTranslation(
                          watchedPriceType,
                        )}
                      />
                    </Text>
                  </Flex>
                )}
              </Col>
              <Col
                col={9}
                className='d-flex align-items-center justify-content-end'
              >
                <Text heading='xs' weight='semi-bold'>
                  <Flex>
                    <span>
                      <Trans i18nKey='app.quote.pricing_policy.form.step1.refine_by' />
                    </span>
                    <Tooltip
                      label={
                        <Trans i18nKey='app.quote.pricing_policy.form.step1.refine_by_helper' />
                      }
                    >
                      <Icon icon='information-line' size='xxs' />
                    </Tooltip>
                  </Flex>
                </Text>
                <Flex>
                  <SwitchField
                    name={`refinementFlags.rim`}
                    variant='DOUBLE'
                    leftLabel=''
                    rightLabel={
                      <Trans i18nKey='app.quote.pricing_policy.service.mounting.refinement.rim' />
                    }
                    size='md'
                    formGroupClassName=''
                    onChangeValue={(checked) => {
                      if (checked)
                        methods.setValue(
                          'rims',
                          pricingPolicyForm.rims.optionItems.reduce(
                            (acc, curr) => {
                              acc[curr.value] = {
                                refinements: [{ tyreCount: {} }],
                              };
                              return acc;
                            },
                            {} as NonNullable<
                              IQuoteServiceMountingStepForm['rims']
                            >,
                          ),
                        );
                    }}
                  />
                  <SwitchField
                    name={`refinementFlags.tyreType`}
                    rightLabel={
                      <Trans i18nKey='app.common.product.vehicule_type' />
                    }
                    leftLabel=''
                    variant='DOUBLE'
                    size='md'
                    formGroupClassName=''
                  />
                  <SwitchField
                    name={`refinementFlags.diameter`}
                    rightLabel={
                      <Trans i18nKey='app.common.product.refinement_diameter' />
                    }
                    leftLabel=''
                    variant='DOUBLE'
                    size='md'
                    formGroupClassName=''
                  />
                  <SwitchField
                    name={`refinementFlags.tyreCount`}
                    rightLabel={
                      <Trans i18nKey='app.quote.pricing_policy.service.mounting.refinement.tyre_count' />
                    }
                    leftLabel=''
                    variant='DOUBLE'
                    size='md'
                    formGroupClassName=''
                  />
                </Flex>
              </Col>
            </Row>
            <Row>
              <Col sm={12}>
                {hasRefinement && (
                  <>
                    {watchedRefinementFlags.rim ? (
                      <>
                        {QuoteServicePricingPolicyHelpers.getRims(
                          pricingPolicyForm.rims.optionItems,
                          watchedRefinementFlags,
                        ).map((rim) => (
                          <div key={rim.value} className='mt-3 space-y-2'>
                            <Text weight='semi-bold'>{rim.label}</Text>
                            <QuoteRefinementTable rim={rim} />
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className='mt-3'>
                        <QuoteRefinementTable
                          rim={{
                            label:
                              CommonPricingPolicyHelpers.NO_REFINEMENT_VALUE,
                            value:
                              CommonPricingPolicyHelpers.NO_REFINEMENT_VALUE,
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </Col>
            </Row>
          </Row>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default QuoteMountingStepForm;
